import {
  CheckSquare, Activity, MonitorCheck, Users, Settings,
  Shield, Clock, DollarSign, Boxes, Truck, ShoppingCart, Factory,
  Search, ClipboardList, Map, LifeBuoy, Target, Workflow, Database, GitBranch
} from "lucide-react";

export const COMPANY = {
  name: "Montivex",
  full: "Montivex Supply Chain Solutions",
  email: "info@montivexsupplychain.com",
  phone: "(929) 564-3961",
  responseTime: "Within 1 business day",
};

export const SERVICES = [
  {
    slug: "wms-health-assessment",
    icon: CheckSquare,
    title: "WMS Health Assessment",
    short: "A thorough review of your warehouse management environment to identify risks, inefficiencies, and opportunities — delivered with a prioritized 30/60/90-day roadmap.",
    points: [
      "Operational process assessment",
      "Inventory control review",
      "System & integration review",
      "Reporting assessment",
    ],
    cta: "Get an Assessment",
  },
  {
    slug: "warehouse-optimization",
    icon: Activity,
    title: "Warehouse Optimization",
    short: "Improve warehouse performance through targeted process and system optimization — from receiving through shipping, with measurable improvements in labor and accuracy.",
    points: [
      "Picking & putaway optimization",
      "Labor productivity improvement",
      "Slotting analysis",
      "KPI development & tracking",
    ],
    cta: "Optimize Your Warehouse",
  },
  {
    slug: "wms-implementation",
    icon: MonitorCheck,
    title: "WMS Implementation Consulting",
    short: "Expert support through every phase of a WMS implementation or warehouse system transformation — from discovery through hypercare and beyond.",
    points: [
      "Discovery & requirements gathering",
      "Configuration & gap analysis",
      "UAT management & test scripts",
      "Go-live & hypercare support",
    ],
    cta: "Plan Your Implementation",
  },
  {
    slug: "fractional-wms-analyst",
    icon: Users,
    title: "Fractional WMS Analyst",
    short: "On-demand WMS expertise without the cost of a full-time analyst. Flexible monthly retainers — 10, 20, or 40 hours per month — to support your team exactly where needed.",
    points: [
      "User & configuration support",
      "Inventory troubleshooting",
      "Process documentation",
      "Vendor coordination",
    ],
    cta: "Explore Retainer Options",
  },
  {
    slug: "fractional-systems-analyst",
    icon: Settings,
    title: "Fractional Supply Chain Systems Analyst",
    short: "Act as an extension of your supply chain systems team — providing expert coverage across WMS, TMS, ERP, EDI, API integrations, and reporting platforms on a flexible monthly basis.",
    points: [
      "Integration support & root-cause analysis",
      "Data analysis & dashboard development",
      "Vendor management",
      "Reporting platforms & dashboards",
    ],
    cta: "Learn More",
  },
];

export const INDUSTRIES = [
  {
    icon: "🏭",
    title: "Third-Party Logistics (3PL)",
    pains: [
      "Inventory accuracy across multiple clients",
      "Complex client onboarding processes",
      "WMS support & configuration demands",
      "Reporting visibility for client SLAs",
      "Labor efficiency management",
    ],
  },
  {
    icon: "📦",
    title: "Distributors",
    pains: [
      "Manual and paper-based processes",
      "Inventory discrepancies & shrinkage",
      "Legacy systems limiting growth",
      "Scaling warehouse capacity",
      "System integration gaps",
    ],
  },
  {
    icon: "🛒",
    title: "Ecommerce Fulfillment",
    pains: [
      "Order volume growth & peak spikes",
      "Picking speed and accuracy",
      "Real-time inventory visibility",
      "Customer service issue resolution",
      "Returns processing complexity",
    ],
  },
  {
    icon: "⚙️",
    title: "Manufacturers",
    pains: [
      "Warehouse modernization initiatives",
      "System integrations (WMS ↔ ERP)",
      "Inventory management accuracy",
      "Raw material & finished goods visibility",
      "Compliance & traceability requirements",
    ],
  },
];

export const PROCESS = [
  { n: "01", title: "Discovery Call", text: "We start with a focused conversation about your current environment, pain points, and goals — no sales pitch, just an honest assessment of where you are." },
  { n: "02", title: "Current-State Assessment", text: "We review your systems, processes, integrations, and operational workflows to build a clear picture of what's working and what's holding you back." },
  { n: "03", title: "Solution & Roadmap", text: "You receive a prioritized plan with specific recommendations, defined deliverables, and a practical timeline — whether that's a one-time project or an ongoing engagement." },
  { n: "04", title: "Execution & Support", text: "We work alongside your team through implementation, testing, go-live, and beyond — with hands-on support at every milestone until results are realized." },
];

export const APPROACH = [
  { icon: Search, title: "We ask the questions you wouldn't ask yourself", text: "An outside perspective surfaces the assumptions baked into daily operations. We dig into the why behind every process before recommending change — so solutions fit your reality, not a template." },
  { icon: Database, title: "Every recommendation is data-driven", text: "We anchor decisions in your actual order profiles, inventory data, labor metrics, and system logs. No guesswork — just evidence that builds confidence and a defensible business case." },
  { icon: Target, title: "We design for measurable ROI", text: "Each engagement is scoped around outcomes you can put a number on: accuracy gains, labor savings, throughput, and reduced downtime. If it doesn't move a metric, it doesn't make the roadmap." },
  { icon: Workflow, title: "We work alongside your team — not around it", text: "Sustainable change requires buy-in from the floor to the front office. We partner with your people, transfer knowledge, and leave your team stronger than we found it." },
];

export const VALUES = [
  { icon: Shield, title: "Operations + Technology", text: "We bring together warehouse floor expertise and enterprise systems knowledge — the combination most consultants are missing." },
  { icon: Clock, title: "Practical, Not Theoretical", text: "We focus on real-world execution and measurable outcomes — not lengthy consulting engagements that drain budgets without results." },
  { icon: DollarSign, title: "Enterprise Expertise, Accessible Costs", text: "Enterprise-level supply chain knowledge without enterprise-level consulting costs — built for growing warehouses, distributors, and 3PLs." },
];

export const CASE_STUDIES = [
  {
    tag: "3PL · WMS Optimization",
    title: "Multi-client 3PL eliminates recurring inventory discrepancies",
    challenge: "A growing 3PL struggled with cycle-count discrepancies across multiple client accounts, eroding SLA performance and consuming hours of manual reconciliation each week.",
    solution: "We restructured location logic, redesigned the cycle-count program, and rebuilt client-facing inventory reporting directly from the WMS.",
    results: [
      { num: "99.4%", label: "Inventory accuracy" },
      { num: "12 hrs", label: "Saved weekly" },
    ],
  },
  {
    tag: "Distribution · Process Improvement",
    title: "Distributor doubles pick throughput without adding headcount",
    challenge: "A regional distributor relied on paper picking and static slotting, capping throughput during peak and driving overtime costs.",
    solution: "We introduced a slotting analysis, optimized pick paths, and configured directed picking in the existing WMS — no new software required.",
    results: [
      { num: "2.1x", label: "Pick throughput" },
      { num: "31%", label: "Lower labor cost" },
    ],
  },
  {
    tag: "Ecommerce · Systems Integration",
    title: "Ecommerce brand gains real-time inventory across channels",
    challenge: "Disconnected systems caused overselling and stockouts during promotions, with no single source of truth for available-to-promise inventory.",
    solution: "We mapped and stabilized the WMS↔ERP↔marketplace integrations, added EDI monitoring, and built a live inventory dashboard.",
    results: [
      { num: "0", label: "Oversells at peak" },
      { num: "Real-time", label: "Inventory visibility" },
    ],
  },
  {
    tag: "Manufacturing · WMS Implementation",
    title: "Manufacturer modernizes the warehouse behind a WMS go-live",
    challenge: "A manufacturer planning a new WMS lacked internal bandwidth and a clear path from legacy processes to a modern, scanned environment.",
    solution: "We led discovery, configuration, UAT, and hypercare — acting as the bridge between operations, IT, and the software vendor through go-live.",
    results: [
      { num: "On-time", label: "Go-live delivered" },
      { num: "30/60/90", label: "Stabilization plan" },
    ],
  },
];

export const SYSTEMS = ["WMS", "TMS", "ERP", "EDI", "API Integrations", "Reporting Platforms", "Warehouse Implementations", "Process Optimization", "Inventory Control", "KPI Development"];

// WMS / supply chain platforms we consult on — also strong SEO keywords.
export const PLATFORMS = [
  "Manhattan Associates", "Blue Yonder (JDA)", "Körber (HighJump)", "SAP EWM",
  "Oracle WMS", "Infor WMS", "NetSuite WMS", "Manhattan SCALE", "Softeon", "Deposco",
];

// Supply-chain themed imagery (Unsplash).
export const IMAGES = {
  homeBand: "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400&ixlib=rb-4.1.0",
  about: "https://images.unsplash.com/photo-1781559818983-4180d744416a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400&ixlib=rb-4.1.0",
  services: "https://images.unsplash.com/photo-1777026321659-64941fb943dd?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400&ixlib=rb-4.1.0",
  industries: "https://images.unsplash.com/photo-1627309366653-2dedc084cdf1?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400&ixlib=rb-4.1.0",
  cases: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400&ixlib=rb-4.1.0",
  process: "https://images.unsplash.com/photo-1622127739239-1905bbaa21b8?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400&ixlib=rb-4.1.0",
  contact: "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400&ixlib=rb-4.1.0",
};

// Per-page SEO metadata. Keywords blend high-intent search terms with
// category / competitor cross-references to widen discoverability.
const COMPETITORS = "supply chain consulting firm, alternative to Alpine Supply Chain Solutions, enVista, Tompkins Solutions, St. Onge, Fortna, Bricz, open sky group";
const CORE_KW = "WMS consultant, warehouse management system consulting, warehouse optimization, supply chain systems integration, 3PL consulting, fractional supply chain analyst, Manhattan Associates consultant, Blue Yonder implementation, Körber WMS, slotting analysis, labor management system, EDI integration, inventory accuracy";

export const PAGE_SEO = {
  home: {
    title: "WMS Consulting & Warehouse Optimization",
    description: "Montivex helps warehouses, distributors, and 3PLs optimize WMS technology and warehouse operations through consulting, process improvement, and fractional supply chain analyst services.",
    keywords: `${CORE_KW}, ${COMPETITORS}`,
  },
  about: {
    title: "About",
    description: "Montivex bridges warehouse operations and enterprise systems — practical, data-driven supply chain consulting with measurable ROI, an accessible alternative to large consulting firms.",
    keywords: `about supply chain consulting, ${CORE_KW}, ${COMPETITORS}`,
  },
  services: {
    title: "Services — WMS Consulting & Warehouse Optimization",
    description: "WMS health assessments, warehouse optimization, WMS implementation consulting, and fractional WMS & supply chain systems analyst services for 3PLs, distributors, and manufacturers.",
    keywords: `WMS health assessment, WMS implementation consulting, warehouse optimization consultant, ${CORE_KW}`,
  },
  industries: {
    title: "Industries — 3PL, Distribution, Ecommerce & Manufacturing",
    description: "Supply chain systems consulting built for 3PLs, distributors, ecommerce fulfillment, and manufacturers — solving inventory, integration, and warehouse scaling challenges.",
    keywords: `3PL consulting, distribution center optimization, ecommerce fulfillment consulting, manufacturing warehouse, ${CORE_KW}`,
  },
  cases: {
    title: "Case Studies & Results",
    description: "Real, measurable supply chain results: inventory accuracy, labor cost reduction, throughput gains, and successful WMS go-lives across 3PL, distribution, ecommerce, and manufacturing.",
    keywords: `supply chain case studies, WMS optimization results, warehouse ROI, ${CORE_KW}`,
  },
  process: {
    title: "Our Process — How We Work",
    description: "A clear, data-driven supply chain consulting process: discovery, current-state assessment, prioritized roadmap, and hands-on execution with knowledge transfer.",
    keywords: `supply chain consulting process, warehouse assessment, WMS roadmap, ${CORE_KW}`,
  },
  contact: {
    title: "Contact — Schedule a Discovery Call",
    description: "Tell us about your warehouse or supply chain systems challenge. Free 60-minute discovery call. We respond within one business day. info@montivexsupplychain.com.",
    keywords: `contact supply chain consultant, schedule WMS consultation, ${CORE_KW}`,
  },
};

